import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CRMContext = createContext(null);

export const useCRM = () => {
  const context = useContext(CRMContext);
  if (!context) {
    throw new Error("useCRM must be used within a CRMProvider");
  }
  return context;
};

export const CRMProvider = ({ children }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastFetched, setLastFetched] = useState(null);

  // Fetch all clients
  const fetchClients = useCallback(async (force = false) => {
    // Don't refetch if data is less than 30 seconds old (unless forced)
    if (!force && lastFetched && Date.now() - lastFetched < 30000) {
      return clients;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API}/crm/clients`);
      setClients(response.data || []);
      setLastFetched(Date.now());
      return response.data;
    } catch (error) {
      console.error("Failed to fetch clients:", error);
      return [];
    } finally {
      setLoading(false);
    }
  }, [clients, lastFetched]);

  // Get client by ID or email
  const getClient = useCallback(async (clientId) => {
    try {
      const response = await axios.get(`${API}/crm/clients/${clientId}`);
      return response.data;
    } catch (error) {
      console.error("Failed to fetch client:", error);
      return null;
    }
  }, []);

  // Create new client
  const createClient = useCallback(async (clientData) => {
    try {
      const response = await axios.post(`${API}/crm/clients`, clientData);
      // Refresh the clients list
      await fetchClients(true);
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Failed to create client:", error);
      return { 
        success: false, 
        error: error.response?.data?.detail || "Failed to create client" 
      };
    }
  }, [fetchClients]);

  // Update client
  const updateClient = useCallback(async (clientId, clientData) => {
    try {
      const response = await axios.put(`${API}/crm/clients/${clientId}`, clientData);
      // Refresh the clients list
      await fetchClients(true);
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Failed to update client:", error);
      return { 
        success: false, 
        error: error.response?.data?.detail || "Failed to update client" 
      };
    }
  }, [fetchClients]);

  // Add note to client
  const addClientNote = useCallback(async (clientId, note) => {
    try {
      await axios.post(`${API}/crm/clients/${clientId}/notes`, { note });
      return { success: true };
    } catch (error) {
      console.error("Failed to add note:", error);
      return { 
        success: false, 
        error: error.response?.data?.detail || "Failed to add note" 
      };
    }
  }, []);

  // Delete note from client
  const deleteClientNote = useCallback(async (clientId, noteId) => {
    try {
      await axios.delete(`${API}/crm/clients/${clientId}/notes/${noteId}`);
      return { success: true };
    } catch (error) {
      console.error("Failed to delete note:", error);
      return { 
        success: false, 
        error: error.response?.data?.detail || "Failed to delete note" 
      };
    }
  }, []);

  // Search clients locally
  const searchClients = useCallback((query) => {
    if (!query) return clients;
    const lowerQuery = query.toLowerCase();
    return clients.filter(client => 
      client.name?.toLowerCase().includes(lowerQuery) ||
      client.email?.toLowerCase().includes(lowerQuery) ||
      client.phone?.includes(query)
    );
  }, [clients]);

  // Get client options for dropdowns
  const getClientOptions = useCallback(() => {
    return clients.map(client => ({
      value: client.id || client.email,
      label: client.name,
      email: client.email,
      phone: client.phone,
      status: client.status
    }));
  }, [clients]);

  // Initial fetch
  useEffect(() => {
    const adminAuth = sessionStorage.getItem("adminAuth");
    if (adminAuth === "true") {
      fetchClients();
    }
  }, []);

  const value = {
    clients,
    loading,
    fetchClients,
    getClient,
    createClient,
    updateClient,
    addClientNote,
    deleteClientNote,
    searchClients,
    getClientOptions,
    refreshClients: () => fetchClients(true)
  };

  return (
    <CRMContext.Provider value={value}>
      {children}
    </CRMContext.Provider>
  );
};

export default CRMContext;
